import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '@/components/layout/PageLayout';
import { PermissionGuard } from '@/components/layout';
import Table from '@/components/ui/tables/Table';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { LinkButton } from '@/components/ui/buttons';
import { Card } from '@/components/ui/cards';
import { Skeleton } from '@/components/ui/loading';
import { IconScout } from '@/components/ui/images/IconScout';
import { Banner } from '@/components/ui/banners';
import { colors } from '@/assets/styles/colors';
import { EmployeeDimensionalFilter } from '@/components/features/attendance/EmployeeDimensionalFilter';
import { usePaymentConceptsList } from '@/hooks/payment';


export const PaymentConceptsPage: React.FC = () => {
  const { t: translate } = useTranslation('payment');
  const {
    selectedCompanyId,
    setSelectedCompanyId,
    companyOptions,
    year,
    month,
    monthlyCutId,
    setYear,
    setMonth,
    setMonthlyCutId,
    yearOptions,
    monthOptions,
    monthlyCutOptions,
    loadingMonthlyCuts,
    handleSearch,
    hasSearched,
    data,
    totalRecords,
    isLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    columns,
    noCutsAvailable,
    noCutsMessageParts,
    dimensionalConfig,
    dimensionalFilter,
    isCutRecalculating,
    isCutBlocked,
    recalculationMessageParts,
    onDownload,
    isExporting,
    canDownload,
  } = usePaymentConceptsList();

  const {
    isExpanded: isAdvancedExpanded,
    toggleExpanded: toggleAdvancedExpanded,
  } = dimensionalFilter;
  // Solo el recálculo real (isCutRecalculating) debe ocultar el panel — usar isCutBlocked aquí
  // desmontaría/colapsaría el panel también durante el chequeo transitorio de cada cambio de
  // filtro principal (isCheckingCutStatus), perdiendo la selección del usuario sin necesidad.
  const showAdvancedContent = isAdvancedExpanded && !noCutsAvailable && !isCutRecalculating;

  const [dismissedNoCutsScope, setDismissedNoCutsScope] = useState<string | null>(null);
  const showNoCutsMessage = noCutsAvailable && selectedCompanyId !== dismissedNoCutsScope;

  // El descarte se ancla al corte: cambiar a otro corte bloqueado vuelve a mostrar el mensaje.
  const [dismissedRecalculationCutId, setDismissedRecalculationCutId] = useState<
    number | undefined
  >(undefined);
  const showRecalculationMessage =
    isCutRecalculating && monthlyCutId !== dismissedRecalculationCutId;

  return (
    <PageLayout dynamicTitle={translate('list.title')}>
      <div className="flex flex-col gap-6" data-testid="payment-concepts-list-page">
        {showRecalculationMessage && (
          <Banner
            variant="warning"
            density="compact"
            text={recalculationMessageParts}
            onDismiss={() => setDismissedRecalculationCutId(monthlyCutId)}
            dismissLabel={translate('list.actions.dismissMessage')}
            data-testid="payment-concepts-recalculating-message"
          />
        )}

        {showNoCutsMessage && (
          <Banner
            variant="error"
            density="compact"
            icon="info"
            text={noCutsMessageParts}
            onDismiss={() => setDismissedNoCutsScope(selectedCompanyId)}
            dismissLabel={translate('list.actions.dismissMessage')}
            data-testid="payment-concepts-no-cuts-message"
          />
        )}

        <Card padding="2rem">
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div data-testid="filter-field-company">
                <Selector
                  label={translate('list.filters.company')}
                  value={selectedCompanyId}
                  onChange={(value) => {
                    const stringValue = Array.isArray(value) ? value[0] : value;
                    setSelectedCompanyId(stringValue ?? '');
                  }}
                  options={companyOptions}
                  data-testid="payment-concepts-company-selector"
                />
              </div>

              <div data-testid="filter-field-year">
                {loadingMonthlyCuts ? (
                  <div className="flex items-center justify-center h-full min-h-[42px]">
                    <Skeleton />
                  </div>
                ) : (
                  <Selector
                    label={translate('list.filters.year')}
                    placeholder={translate('list.filters.selectYear')}
                    value={year ? String(year) : ''}
                    onChange={(value) => setYear(Number(value) || undefined)}
                    options={yearOptions}
                    isLoading={loadingMonthlyCuts}
                    disabled={noCutsAvailable}
                    data-testid="payment-concepts-year-selector"
                  />
                )}
              </div>

              <div data-testid="filter-field-month">
                {loadingMonthlyCuts ? (
                  <div className="flex items-center justify-center h-full min-h-[42px]">
                    <Skeleton />
                  </div>
                ) : (
                  <Selector
                    label={translate('list.filters.month')}
                    placeholder={translate('list.filters.selectMonth')}
                    value={month ? String(month) : ''}
                    onChange={(value) => setMonth(Number(value) || undefined)}
                    options={monthOptions}
                    disabled={noCutsAvailable || !year}
                    isLoading={loadingMonthlyCuts}
                    data-testid="payment-concepts-month-selector"
                  />
                )}
              </div>

              <div data-testid="filter-field-description">
                {loadingMonthlyCuts ? (
                  <div className="flex items-center justify-center h-full min-h-[42px]">
                    <Skeleton />
                  </div>
                ) : (
                  <Selector
                    key={`monthly-cut-${year}-${month}`}
                    label={translate('list.filters.description')}
                    placeholder={translate('list.filters.selectDescription')}
                    value={monthlyCutId ? String(monthlyCutId) : ''}
                    onChange={(value) => {
                      const stringValue = Array.isArray(value) ? value[0] : value;
                      setMonthlyCutId(stringValue ? Number(stringValue) : undefined);
                    }}
                    options={monthlyCutOptions}
                    disabled={noCutsAvailable || !year || !month}
                    isLoading={loadingMonthlyCuts}
                    data-testid="payment-concepts-description-selector"
                  />
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={toggleAdvancedExpanded}
              disabled={noCutsAvailable || isCutRecalculating}
              className="self-start flex items-center gap-1 font-medium text-sm cursor-pointer bg-transparent border-none p-0 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: colors.important.main }}
              aria-expanded={showAdvancedContent}
              data-testid="payment-concepts-toggle-advanced-filters"
            >
              {showAdvancedContent
                ? translate('list.actions.hideAdvancedFilters')
                : translate('list.actions.showAdvancedFilters')}
              <IconScout
                name={showAdvancedContent ? 'angleUp' : 'angleDown'}
                size={16}
              />
            </button>

            {showAdvancedContent && (
              <>
                <EmployeeDimensionalFilter
                  config={dimensionalConfig}
                  state={dimensionalFilter}
                  companyLocked
                />

                <div className="self-end">
                  <PrimaryButton
                    onClick={handleSearch}
                    disabled={!monthlyCutId || isCutBlocked}
                    data-testid="payment-concepts-search-button"
                  >
                    {translate('list.actions.search')}
                  </PrimaryButton>
                </div>
              </>
            )}
          </div>
        </Card>

        <PermissionGuard action="view">
          <div className="flex justify-end">
            <LinkButton
              iconLeft="import"
              onClick={() => onDownload()}
              disabled={!canDownload}
              isLoading={isExporting}
              data-testid="payment-concepts-download-button"
            >
              {translate('list.actions.download')}
            </LinkButton>
          </div>
        </PermissionGuard>

        <Table
          columns={columns}
          data={data}
          totalRows={totalRecords}
          isLoading={isLoading}
          searchable
          onSearch={onSearch}
          serverSideSort
          onSortChange={onSortChange}
          pagination={{
            page,
            pageSize,
            total: totalRecords,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          initialSort={{ key: 'generatedAt', direction: 'desc' }}
          emptyMessage={
            hasSearched || noCutsAvailable || isCutBlocked
              ? undefined
              : translate('list.messages.selectFilters')
          }
          data-testid="table-payment-concepts"
        />
      </div>
    </PageLayout>
  );
};

export default PaymentConceptsPage;
